import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Card,
  Form,
  Button,
  Space,
  Typography,
  Spin,
  Alert,
  Checkbox,
  Divider,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SendOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { getTasks, createReport, updateReport, getCurrentWeekStatus, getReport } from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';

const { Title } = Typography;

export default function NewReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draftId');
  const [form] = Form.useForm();
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tasksData, currentStatus] = await Promise.all([
        getTasks({ status: 'completed' }),
        getCurrentWeekStatus(),
      ]);
      setTasks(tasksData);

      const reportIdToLoad = draftId
        ? parseInt(draftId)
        : currentStatus.reportId && currentStatus.status === 'draft'
        ? currentStatus.reportId
        : null;

      if (reportIdToLoad) {
        const report = await getReport(reportIdToLoad);
        // Delay setFieldsValue to ensure form is mounted
        setTimeout(() => {
          form.setFieldsValue({
            completedTasks: report.completedTasks || '',
            nextWeekPlan: report.nextWeekPlan || '',
            improvements: report.improvements || '',
            selectedTaskIds: report.reportTasks?.map((rt: any) => rt.taskId) || [],
          });
        }, 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any, submit = false) => {
    if (submit) {
      setSubmitting(true);
    } else {
      setSaving(true);
    }
    setError('');

    try {
      const data = {
        completedTasks: values.completedTasks,
        nextWeekPlan: values.nextWeekPlan,
        improvements: values.improvements,
        taskIds: values.selectedTaskIds || [],
        submit,
      };

      if (draftId) {
        await updateReport(parseInt(draftId), data);
      } else {
        await createReport(data);
      }
      navigate('/reports');
    } catch (err: any) {
      setError(err.message || '儲存失敗');
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  const getWeekStart = () => {
    const d = new Date();
    const day = d.getUTCDay();
    const diff = d.getUTCDate() - day;
    d.setUTCDate(diff);
    d.setUTCHours(0, 0, 0, 0);
    return d;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <Link to="/reports">
        <Button icon={<ArrowLeftOutlined />}>返回週報列表</Button>
      </Link>

      <Card>
        <Title level={2}>
          <FileTextOutlined /> {draftId ? '編輯週報草稿' : '填寫週報'}
        </Title>

        <Alert
          title={`週次: ${getWeekStart().toLocaleDateString('zh-TW')} - 本週`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {error && (
          <Alert title={error} type="error" showIcon style={{ marginBottom: 16 }} />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => handleSave(values, false)}
          initialValues={{ selectedTaskIds: [] }}
        >
          <Divider>本週完成事項</Divider>

          {tasks.length > 0 && (
            <Form.Item name="selectedTaskIds" label="選擇已完成的任務">
              <Checkbox.Group style={{ width: '100%' }}>
                <Space orientation="vertical" style={{ width: '100%' }}>
                  {tasks.map((task) => (
                    <Checkbox key={task.id} value={task.id}>
                      {task.title}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </Form.Item>
          )}

          <Form.Item name="completedTasks" label="其他完成事項">
            <RichTextEditor
              placeholder="描述本週完成的其他工作項目..."
              height={150}
            />
          </Form.Item>

          <Divider>下週計畫</Divider>

          <Form.Item name="nextWeekPlan">
            <RichTextEditor
              placeholder="描述下週的工作計畫..."
              height={150}
            />
          </Form.Item>

          <Divider>改善建議</Divider>

          <Form.Item name="improvements">
            <RichTextEditor
              placeholder="分享您對流程改善的建議或想法（選填）..."
              height={120}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Link to="/reports">
                <Button>取消</Button>
              </Link>
              <Button
                icon={<SaveOutlined />}
                loading={saving}
                onClick={() => form.submit()}
              >
                儲存草稿
              </Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={submitting}
                onClick={() => {
                  form.validateFields().then((values) => {
                    handleSave(values, true);
                  });
                }}
              >
                提交週報
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </Space>
  );
}
